import {
   type AnalyticsItem,
   type AnalyticsItemsEvent,
   type CurrencyCode,
   toUSD,
   trackAnalyticsCustomEvent,
} from '@ifixit/analytics';
import { getProductIdFromGlobalId, getVariantIdFromVariantURI } from '@ifixit/helpers';
import { AddToCartInput } from '../hooks/cart/use-add-to-cart';
import { type BillingInterval, type BillingPolicy, type CartLineItem } from '../types';

const ADD_TO_CART_EVENT = 'Add to Cart';

export function trackAnalyticsShopifyAddToCart({ lines, analytics }: AddToCartInput) {
   const itemcodes =
      analytics.descriptor === 'Frequently Bought Together'
         ? `${analytics.currentItemcode}/${lines[0].itemcode}`
         : lines.map(line => line.itemcode).join(', ');
   const totalPriceUSD = lines.reduce((sum, line) => {
      const lineUSD = toUSD(Number(line.price.amount), line.price.currencyCode as CurrencyCode);
      const linePrice = line.quantity * lineUSD;
      return sum + linePrice;
   }, 0);
   // Subscriptions only. Sending the key on every add to cart would put an
   // empty param on the hundreds of one-time-purchase events that share this
   // function.
   const billingInterval = getBillingInterval(lines);
   trackAnalyticsCustomEvent({
      eventCategory: ADD_TO_CART_EVENT,
      eventAction: `${ADD_TO_CART_EVENT} - ${analytics.descriptor}`,
      eventName: itemcodes,
      eventValue: totalPriceUSD,
      ...(billingInterval && { billing_interval: billingInterval }),
   });
}

/**
 * The billing cadence for the lines going into one add-to-cart mutation, if
 * they agree on one. Lines spanning a monthly and an annual plan have no
 * single answer, so it reports none rather than picking one arbitrarily.
 */
function getBillingInterval(lines: CartLineItem[]): string | undefined {
   const intervals = new Set(
      lines
         .map(line => formatBillingInterval(line.billingPolicy))
         .filter(interval => interval != null)
   );
   return intervals.size === 1 ? [...intervals][0] : undefined;
}

export function convertAddToCartInputToAnalyticsItemEvent(
   input: AddToCartInput,
   shopifyCartId: string | undefined
): AnalyticsItemsEvent {
   return {
      items: convertCartLineItemsToAnalyticsItem(input.lines),
      value: input.lines.reduce((acc, item) => acc + Number(item.price.amount), 0),
      currency: input.lines[0]?.price?.currencyCode,
      localeCode: input.analytics.localeCode,
      shopifyCartId,
   };
}

export function convertCartLineItemsToAnalyticsItem(items: CartLineItem[]): AnalyticsItem[] {
   return items.map(item => {
      const billingInterval = formatBillingInterval(item.billingPolicy);
      return {
         item_id: item.itemcode,
         item_name: item.internalDisplayName ?? `${item.name} - ${item.variantTitle}`,
         item_variant: getVariantIdFromVariantURI(item.shopifyVariantId),
         numericProductId: getProductIdFromGlobalId(item.shopifyProductId),
         quantity: item.quantity,
         price: Number(item.price.amount),
         categories: item.categories ?? [],
         fulfiller: item.fulfiller,
         oemPartnership: item.oemPartnership?.code,
         ...(billingInterval && { billing_interval: billingInterval }),
      };
   });
}

const SINGLE_INTERVAL_NAMES: Record<BillingInterval, string> = {
   DAY: 'daily',
   WEEK: 'weekly',
   MONTH: 'monthly',
   YEAR: 'annual',
};

/**
 * Turns a Shopify billing policy into the one token analytics groups by.
 *
 * Every-one-period plans get the name people actually use ('monthly',
 * 'annual'); anything else falls back to a readable count ('every_3_months').
 */
export function formatBillingInterval(policy: BillingPolicy | undefined): string | undefined {
   if (!policy) {
      return undefined;
   }
   const { interval, intervalCount } = policy;
   if (intervalCount === 1) {
      return SINGLE_INTERVAL_NAMES[interval];
   }
   return `every_${intervalCount}_${interval.toLowerCase()}s`;
}
